import { playerRowFigures, inningsWord } from "./squadAverage";
import { avgText } from "./recordFigures";

/**
 * Issue #1316 — one linked player's figures line on Schedule a game, in the
 * words of `CoachScheduleGame.dc.html` (round 13). They differ from the squad
 * row's (`CoachSquad.jsx`, left alone): "no dismissal yet" on a batter who has
 * never been out, a lower-case "no innings yet", and a bowler with no innings
 * shows "3 ov · 0 wkts · econ 6.33".
 *
 * Input is one `GET /stats/summary?player_id=` payload. Batting is read the
 * way `playerRowFigures` reads it (average from `innings − not_outs`, never
 * the API's zero-filled `avg`). The strike rate or economy is left off when
 * the API has nothing to divide by (`null`), never printed as a nought.
 *
 * @param {object|null} summary
 * @returns {string}
 */
export function squadFiguresLine(summary) {
  const d = summary || {};
  const f = playerRowFigures(d);
  const srText = f.strikeRate == null ? "" : ` · SR ${f.strikeRate.toFixed(1)}`;

  if (f.innings > 0) {
    if (f.dismissals > 0) return `${inningsWord(f.innings)} · avg ${avgText(f.average)}${srText}`;
    return `${inningsWord(f.innings)} · ${f.totalRuns} runs · no dismissal yet${srText}`;
  }

  const overs = Number(d.total_overs) || 0;
  if (overs > 0) {
    const wkts = Number(d.total_wickets) || 0;
    const econ = d.econ == null ? "" : ` · econ ${Number(d.econ).toFixed(2)}`;
    return `${overs} ov · ${wkts} ${wkts === 1 ? "wkt" : "wkts"}${econ}`;
  }

  return "no innings yet";
}

/**
 * Issue #1317 — one bowling-order row's figures line, in the words of
 * `PickTheXI.dc.html`: "Opening bowler · bats #9 · 26 ov · 11 wkts · econ 4.23"
 * for places 1 and 2, "bats #11 · 31 ov · 9 wkts · econ 5.13" after them.
 *
 * Overs, wickets and economy come from the player's `/stats/summary`
 * (`total_overs`, `total_wickets`, `econ`). Economy is two decimals (D190)
 * and is left off when the API has nothing to divide by, never printed as a
 * nought; a player who has not bowled shows the place they bat at only.
 * With no summary (still loading, or it failed) the line is that same
 * place-only form.
 *
 * @param {object|null} summary
 * @param {{ opening: boolean, batsAt: number }} where
 * @returns {string}
 */
export function bowlingFiguresLine(summary, { opening, batsAt }) {
  const d = summary || {};
  const parts = [];
  if (opening) parts.push("Opening bowler");
  parts.push(`bats #${batsAt}`);
  const overs = Number(d.total_overs) || 0;
  if (overs > 0) {
    const wkts = Number(d.total_wickets) || 0;
    parts.push(`${overs} ov`, `${wkts} ${wkts === 1 ? "wkt" : "wkts"}`);
    if (d.econ != null) parts.push(`econ ${Number(d.econ).toFixed(2)}`);
  }
  return parts.join(" · ");
}
